import { Form, Formik } from 'formik';
import React, { useContext, useMemo, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import Button from '../../../../components/common/Button';
import InputImage from '../../../../components/forms/Input/inputImage';
import InputWrapper from '../../../../components/forms/Input/inputWrapper';
import { ProjectContext } from '../../projectContext';
import InputColor from './../../../../components/forms/Input/InputColor';
import Input from '../../../../components/forms/Input';
import * as Yup from 'yup';
import draftToHtml from 'draftjs-to-html';
import {
  ContentState,
  convertFromHTML,
  convertToRaw,
  EditorState,
} from 'draft-js';
import { ACTION } from '../../../../reducer/projectReducer';
import {
  InfoOutlined,
  PaletteOutlined,
  ViewQuiltOutlined,
} from '@material-ui/icons';
import {
  YupEditorMaxLengthValidator,
  YupEditorMinLengthValidator,
} from '../../../../utils/validators';
import openAnnouncementWebPreview from '../preview/openAnnouncementWebPreview';

const ALLOWED_CHAR = 300;
const SECTION_OPTIONS = [
  { value: 'profile', label: 'Perfil' },
  { value: 'home', label: 'Inicio' },
  { value: 'mentorships', label: 'Mentorías' },
  { value: 'volunteering', label: 'Voluntariado Coorporativo' },
  { value: 'courses', label: 'Cursos' },
  { value: 'workshops', label: 'Talleres' },
  { value: 'companies', label: 'Pymes' },
  { value: 'catalog', label: 'CompraPyme' },
  { value: 'news', label: 'Novedades' },
  { value: 'evaluador', label: 'Evaluador' }
];
const SECTION_VALUES = new Set(SECTION_OPTIONS.map(({ value }) => value));

const normalizeSections = (sections = []) => {
  const selectedSections = [];

  sections.forEach((section) => {
    const value = typeof section === 'string' ? section : section?.value;

    if (SECTION_VALUES.has(value) && !selectedSections.includes(value)) {
      selectedSections.push(value);
    }
  });

  return selectedSections;
};

const normalizePlatformSections = (platform) => ({
  ...platform,
  sections: Array.isArray(platform?.sections)
    ? normalizeSections(platform.sections)
    : [],
});

const PlatformForm = ({ onPrev, onNext }) => {
  YupEditorMaxLengthValidator();
  YupEditorMinLengthValidator();
  const [state, dispatch] = useContext(ProjectContext);
  const { platform } = state;
  const platformValues = useMemo(
    () => normalizePlatformSections(platform),
    [platform]
  );
  const defaultWelcomeText = state.platform.welcomeMessage;
  const blockFromHTML = convertFromHTML(defaultWelcomeText);
  const content = ContentState.createFromBlockArray(blockFromHTML);

  const [textEditor, setTextEditor] = useState(
    EditorState.createWithContent(content)
  );

  const schema = Yup.object().shape({
    platformPrimaryColor: Yup.string().required(
      'Debes ingresar un color primario'
    ),
    platformSecondaryColor: Yup.string().required(
      'Debes ingresar un color secundario'
    ),
    platformBanner: Yup.string().required(
      'Debes ingresar un banner para la plataforma '
    ),
    welcomeMessage: Yup.string()
      .editorMinLengthValidator(1, 'Debes ingresar un texto de bienvenida')
      .editorMaxLengthValidator(
        ALLOWED_CHAR,
        `El largo máximo para la descripción son de ${ALLOWED_CHAR} caracteres`
      )
      .required('Debes ingresar un texto de bienvenida'),
  });

  const handleOnPrev = (values) => {
    const nextPlatform = {
      ...normalizePlatformSections(values),
      welcomeMessage: draftToHtml(
        convertToRaw(textEditor.getCurrentContent())
      ),
    };

    dispatch({
      type: ACTION.UPDATE_PLATFORM,
      payload: {
        platform: nextPlatform,
      },
    });

    onPrev();
  };

  const handlePreview = (values) => {
    const previewPlatform = {
      ...normalizePlatformSections(values),
      welcomeMessage: draftToHtml(convertToRaw(textEditor.getCurrentContent())),
    };

    dispatch({
      type: ACTION.UPDATE_PLATFORM,
      payload: {
        platform: previewPlatform,
      },
    });

    openAnnouncementWebPreview({
      previewType: 'plataforma',
      previewState: {
        ...state,
        platform: previewPlatform,
      },
    });
  };

  const onSubmit = (values) => {
    const nextPlatform = normalizePlatformSections({
      ...values,
      welcomeMessage: draftToHtml(
        convertToRaw(textEditor.getCurrentContent())
      ),
    });

    dispatch({
      type: ACTION.UPDATE_PLATFORM,
      payload: { platform: nextPlatform },
    });
    onNext();
  };
  return (
    <div className="announcement-form-step">
      <Formik
        initialValues={platformValues}
        validationSchema={schema}
        validateOnBlur
        onSubmit={onSubmit}
      >
        {(props) => {
          return (
            <>
              <Form className="announcement-form announcement-form--platform">
                <div className="wizard__preview-container announcement-form__toolbar">
                  <div className="announcement-form__toolbar-copy">
                    <span className="announcement-wizard__eyebrow">
                      Etapa 2 de 3
                    </span>
                    <h2>Plataforma personalizada</h2>
                    <p>
                      Configura la identidad visual, el contenido de inicio y
                      las secciones disponibles para los participantes.
                    </p>
                  </div>
                  <Button
                    type="button"
                    secondary
                    className="button button--flat"
                    onClick={() => handlePreview(props.values)}
                  >
                    Previsualizar
                  </Button>
                </div>
                <div className="wizard__form">
                  <section className="announcement-form__card">
                    <div className="announcement-form__section-header">
                      <span className="announcement-form__section-icon">
                        <PaletteOutlined />
                      </span>
                      <div>
                        <span className="announcement-wizard__eyebrow">
                          Identidad visual
                        </span>
                        <h3>Colores de la plataforma</h3>
                        <p>
                          Define los tonos que se aplicarán a las acciones,
                          enlaces e íconos de esta convocatoria.
                        </p>
                      </div>
                    </div>
                    <div className="announcement-form__field-grid announcement-form__field-grid--two">
                      <InputWrapper
                        variant="full"
                        label="Color primario"
                        id="primaryColor"
                      >
                        <div className="preview__color-container">
                          <InputColor name="platformPrimaryColor" />
                          <div className="description-container">
                            <InfoOutlined className="info_svg" />
                            <span className="description">
                              El color primario corresponde a todos los botones
                              de acción dentro de la plataforma.
                            </span>
                          </div>
                        </div>
                      </InputWrapper>
                      <InputWrapper
                        variant="full"
                        label="Color secundario"
                        id="secondaryColor"
                      >
                        <div className="preview__color-container">
                          <InputColor name="platformSecondaryColor" />
                          <div className="description-container">
                            <InfoOutlined className="info_svg" />
                            <span className="description">
                              El color secundario corresponde a los iconos que
                              muestra la plataforma.
                            </span>
                          </div>
                        </div>
                      </InputWrapper>
                    </div>
                  </section>

                  <section className="announcement-form__card">
                    <div className="announcement-form__section-header">
                      <span className="announcement-form__section-icon">
                        <ViewQuiltOutlined />
                      </span>
                      <div>
                        <span className="announcement-wizard__eyebrow">
                          Experiencia del participante
                        </span>
                        <h3>Inicio y navegación</h3>
                        <p>
                          Personaliza la portada, el mensaje de bienvenida y
                          las secciones visibles en la plataforma.
                        </p>
                      </div>
                    </div>

                    <div className="announcement-form__field-grid">
                      <InputWrapper
                        variant="big"
                        label="Banner de inicio"
                        description="Razón 1.6:1. Ej: 1600x1000px o 1280x800px. Máximo 5MB. PNG o JPG."
                        id="platformBanner"
                      >
                        <InputImage
                          name="platformBanner"
                          id="platformBanner"
                          allowedExts={[
                            'image/png',
                            'image/jpg',
                            'image/jpeg',
                          ]}
                        />
                      </InputWrapper>
                    </div>

                    <div className="announcement-form__field-grid">
                      <InputWrapper
                        label="Texto de bienvenida"
                        id="welcomeMessage"
                        name="welcomeMessage"
                        variant="big"
                      >
                        <>
                          <Editor
                            id="welcomeMessage"
                            name="welcomeMessage"
                            wrapperClassName="editor__wrapper"
                            toolbarClassName="editor__toolbar"
                            onEditorStateChange={setTextEditor}
                            editorState={textEditor}
                            onBlur={() => {
                              props.setFieldTouched('welcomeMessage', true);
                              props.setFieldValue(
                                'welcomeMessage',
                                draftToHtml(
                                  convertToRaw(textEditor.getCurrentContent())
                                )
                              );
                            }}
                          />

                          <div className="editor__error">
                            {props.touched.welcomeMessage &&
                              props.errors.welcomeMessage && (
                                <span className=" error-message">
                                  {props.errors.welcomeMessage}
                                </span>
                              )}
                            <span className="char-counter">
                              {
                                textEditor.getCurrentContent().getPlainText('')
                                  .length
                              }
                              /{ALLOWED_CHAR}
                            </span>
                          </div>
                        </>
                      </InputWrapper>
                    </div>
                    <div className="announcement-form__field-grid">
                      <InputWrapper
                        variant="big"
                        label="Secciones"
                        description="Selecciona las secciones que quieres mostrar en la plataforma."
                        id="sections"
                      >
                        <Input
                          name="sections"
                          id="sections"
                          type="multiselect"
                          options={SECTION_OPTIONS}
                        />
                      </InputWrapper>
                    </div>
                  </section>
                  <div className="wizard__action">
                    <Button
                      secondary
                      type="button"
                      onClick={() => handleOnPrev(props.values)}
                    >
                      Volver
                    </Button>
                    <Button type="submit">
                      Siguiente
                    </Button>
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default PlatformForm;
